import axios, { type AxiosInstance } from 'axios';
import type {
  LoginRequest,
  LoginResponse,
  User,
  Training,
  TrainingCreate,
  TrainingUpdate,
  Exercise,
  Category,
  PasswordChangeRequest,
  UserProfileUpdate,
} from '../types';

const API_BASE_URL = 'https://api-training.isancosmed.com';
// const API_BASE_URL = 'http://localhost:8000';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests if available
    this.api.interceptors.request.use((config) => {
      // Siempre leer el token más reciente desde la instancia
      const token = this.token || localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Load token from localStorage if available
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      this.token = savedToken;
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  getToken(): string | null {
    return this.token;
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.api.post<LoginResponse>('/user/login', credentials);
      this.setToken(response.data.token);
      return response.data;
    } catch (error) {
      console.error('[ApiService] Error en el login:', error);
      throw error;
    }
  }

  async getUserInfo(): Promise<User> {
    const response = await this.api.get<User>('/user/info');
    return response.data;
  }

  async logout(): Promise<void> {
    this.clearToken();
  }

  // User endpoints
  async updateProfile(data: UserProfileUpdate): Promise<User> {
    const response = await this.api.put<User>('/user/profile', data);
    return response.data;
  }

  async changePassword(data: PasswordChangeRequest): Promise<void> {
    await this.api.put('/user/password', data);
  }

  // Training endpoints
  async getTrainings(): Promise<Training[]> {
    const response = await this.api.get<Training[]>('/trainings');
    return response.data;
  }

  async getTraining(id: number): Promise<Training> {
    const response = await this.api.get<Training>(`/trainings/${id}`);
    return response.data;
  }

  async createTraining(data: TrainingCreate): Promise<Training> {
    const response = await this.api.post<Training>('/trainings', data);
    return response.data;
  }

  async updateTraining(data: TrainingUpdate): Promise<Training> {
    const { id, ...updateData } = data;
    const response = await this.api.put<Training>(`/trainings/${id}`, updateData);
    return response.data;
  }

  async deleteTraining(id: number): Promise<void> {
    await this.api.delete(`/trainings/${id}`);
  }

  // Exercise endpoints
  async getExercises(): Promise<Exercise[]> {
    const response = await this.api.get<Exercise[]>('/exercises');
    return response.data;
  }

  async getExercise(id: number): Promise<Exercise> {
    const response = await this.api.get<Exercise>(`/exercises/${id}`);
    return response.data;
  }

  async createExercise(data: Omit<Exercise, 'id'>): Promise<Exercise> {
    const response = await this.api.post<Exercise>('/exercises', data);
    return response.data;
  }

  async updateExercise(id: number, data: Partial<Omit<Exercise, 'id'>>): Promise<Exercise> {
    const response = await this.api.put<Exercise>(`/exercises/${id}`, data);
    return response.data;
  }

  async deleteExercise(id: number): Promise<void> {
    await this.api.delete(`/exercises/${id}`);
  }

  // Category endpoints
  async getCategories(): Promise<Category[]> {
    const response = await this.api.get<Category[]>('/categories');
    return response.data;
  }

  async getCategory(id: number): Promise<Category> {
    const response = await this.api.get<Category>(`/categories/${id}`);
    return response.data;
  }

  async createCategory(data: Omit<Category, 'id'>): Promise<Category> {
    const response = await this.api.post<Category>('/categories', data);
    return response.data;
  }

  async updateCategory(id: number, data: Partial<Omit<Category, 'id'>>): Promise<Category> {
    const response = await this.api.put<Category>(`/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: number): Promise<void> {
    await this.api.delete(`/categories/${id}`);
  }

  // Coach endpoints
  async getCoachTrainees(): Promise<User[]> {
    const response = await this.api.get<User[]>('/coach/users');
    return response.data;
  }

  async findUsers(searchQuery: string): Promise<User[]> {
    const response = await this.api.get<User[]>('/coach/users/find', {
      params: { name: searchQuery }
    });
    return response.data;
  }

  async linkTrainee(userId: number): Promise<void> {
    await this.api.post(`/coach/link/${userId}`, {});
  }

  async unlinkTrainee(userId: number): Promise<void> {
    await this.api.post(`/coach/unlink/${userId}`, {});
  }

  async getCoachTrainings(): Promise<Training[]> {
    const response = await this.api.get<Training[]>('/coach/trainings');
    return response.data;
  }

  // Admin endpoints
  async getAllUsers(): Promise<User[]> {
    const response = await this.api.get<User[]>('/admin/users');
    return response.data;
  }

  async getAllTrainings(): Promise<Training[]> {
    const response = await this.api.get<Training[]>('/admin/trainings');
    return response.data;
  }

  async createUser(data: { email: string; name: string; username: string; password: string; role: string }): Promise<User> {
    const response = await this.api.post<User>('/admin/user/register', data);
    return response.data;
  }

  async updateUser(id: number, data: Partial<Omit<User, 'id'>>): Promise<User> {
    const response = await this.api.put<User>(`/admin/user/update/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/admin/user/delete/${id}`);
  }

  async changeUserPassword(id: number, newPassword: string): Promise<void> {
    await this.api.put(`/admin/user/password/${id}`, { password: newPassword });
  }
}

export const apiService = new ApiService();
